import React, { useEffect, Suspense, lazy } from 'react';
import { BrowserRouter as Router, Route, Routes } from 'react-router-dom';
import { GoogleOAuthProvider } from '@react-oauth/google';
import LoginPage from './pages/LoginPage';
import Dashboard from './pages/Dashboard/Dashboard';
import GoogleAuthTest from './pages/GoogleAuthTest';
import TodoListPage from './pages/TodoList/TodoListPage';
import ProfilePage from './pages/ProfilePage/ProfilePage';
import ErrorReportsPage from './pages/ErrorReportsPage';
import UsersManagementPage from './pages/UsersManagementPage';
import QualityManagementPage from './pages/QualityManagementPage/QualityManagementPage';
import EmployeeManagementPage from './pages/EmployeeManagement/EmployeeManagementPage';
import PageAccessManagementPage from './pages/PageAccessManagement/PageAccessManagementPage';
import AuthWrapper from './components/AuthWrapper';
import RequireAuthorization from './components/RequireAuthorization';
import Layout from './components/Layout';
import { UserProfileProvider } from './contexts/UserProfileContext'; // Import the user profile context
import ThemeProvider from './contexts/ThemeContext'; // Import the theme context
import { VideoCallProvider } from './components/Teleconference/VideoCallContext'; // Import the video call context
import { PendingLeavesProvider } from './contexts/PendingLeavesContext'; // Bekleyen izin bildirimleri
import { MessageNotificationProvider } from './components/MessageNotifications'; // Uygulama geneli gelen mesaj bildirimi
import './App.css';
import './darkModeFixOverrides.css'; // Import dark mode overrides - this must be after App.css
import OperationManagementPage from './pages/OperationManagement/OperationManagementPage';
import CustomerManagementPage from './pages/CustomerManagement/CustomerManagementPage';
import StockManagementPage from './pages/StockManagementPage';
import ProjectsManagementPage from './pages/ProjectsManagement/ProjectsManagementPage';
import CenterManagementPage from './pages/CenterManagementPage/CenterManagementPage';
import ExternalPersonnelManagementPage from './pages/ExternalPersonnelManagementPage';
import ConstantManagementPage from './pages/ConstantManagementPage';
import SponsorManagementPage from './pages/SponsorManagementPage/SponsorManagementPage';
import EmployeeInsurancePage from './pages/EmployeeManagement/EmployeeInsurancePage';
import CustomerTariffsPage from './pages/CustomerManagement/CustomerTariffsPage';
import BankAccountManagementPage from './pages/FinanceManagement/BankAccountManagementPage';
import SalaryPaymentPage from './pages/FinanceManagement/SalaryPaymentPage';
import BudgetManagementPage from './pages/BudgetManagementPage/BudgetManagementPage';
import SMOLeadReports from './pages/SMOLeadReports';
import BolgeMudurleriReports from './pages/BolgeMudurleriReports';
import EmployeeReports from './pages/EmployeeReports';
import EmployeeAnalysis from './pages/EmployeeAnalysis';
import EmployeeStatistics from './pages/EmployeeStatistics/EmployeeStatistics';
import EducationReportsPage from './pages/EducationManagementPage/EducationReportsPage';
import NotFoundPage from './pages/NotFoundPage';
import DataEntryValuesPage from './pages/DataEntryValuesPage';
import EducationManagementPage from './pages/EducationManagementPage/EducationManagementPage';
import AdminDefinitionsPage from './pages/AdminDefinitionsPage';
import ProjectsFinanceManagementPage from './pages/FinanceManagement/ProjectsFinanceManagement/ProjectsFinanceManagementPage';
import EmployeeFinanceManagementPage from './pages/FinanceManagement/EmployeeFinanceManagement/EmployeeFinanceManagementPage';
const FieldExpensesPage = lazy(() => import('./pages/FinanceManagement/FieldExpensesPage'));
const LeaveRequestsPage = lazy(() => import('./pages/LeaveRequestsPage'));
const BelgeKontrolPage = lazy(() => import('./pages/BelgeKontrol/BelgeKontrolPage'));
const BelgeKontrolVisitPage = lazy(() => import('./pages/BelgeKontrol/VisitDetailPage'));
function App() {
  useEffect(() => {
    console.log('App started!');
    console.log(`Running in ${process.env.NODE_ENV} mode`);
  }, []);

  return (
    <GoogleOAuthProvider 
      clientId={process.env.REACT_APP_GOOGLE_CLIENT_ID}
      onScriptLoadError={() => console.error('Google OAuth script yüklenemedi')}
      onScriptLoadSuccess={() => console.log('Google OAuth script başarıyla yüklendi')}
    >
      <ThemeProvider>
        <UserProfileProvider>
          <VideoCallProvider>
            <PendingLeavesProvider>
            <MessageNotificationProvider>
            <Router>
              <div className="App">
                <div className="main-content">
                  <Routes>
                  <Route path="/" element={<LoginPage />} />
                  <Route path="/google-test" element={<GoogleAuthTest />} />
                  <Route
                    path="/dashboard"
                    element={
                      <AuthWrapper>
                        <RequireAuthorization path="/dashboard">
                          <Layout>
                            <Dashboard />
                          </Layout>
                        </RequireAuthorization>
                      </AuthWrapper>
                    }
                  />
                  <Route
                    path="/todo-list"
                    element={
                      <AuthWrapper>
                        <RequireAuthorization path="/todo-list">
                          <Layout>
                            <TodoListPage />
                          </Layout>
                        </RequireAuthorization>
                      </AuthWrapper>
                    }
                  />
                  <Route
                    path="/profile"
                    element={
                      <AuthWrapper>
                        <RequireAuthorization path="/profile">
                          <Layout>
                            <ProfilePage />
                          </Layout>
                        </RequireAuthorization>
                      </AuthWrapper>
                    }
                  />
                  <Route
                    path="/error-reports"
                    element={
                      <AuthWrapper>
                        <RequireAuthorization path="/error-reports">
                          <Layout>
                            <ErrorReportsPage />
                          </Layout>
                        </RequireAuthorization>
                      </AuthWrapper>
                    }
                  />
                  <Route
                    path="/users-management"
                    element={
                      <AuthWrapper>
                        <RequireAuthorization path="/users-management">
                          <Layout>
                            <UsersManagementPage />
                          </Layout>
                        </RequireAuthorization>
                      </AuthWrapper>
                    }
                  />
                  <Route
                    path="/belge-kontrol"
                    element={
                      <AuthWrapper>
                        <RequireAuthorization path="/belge-kontrol">
                          <Layout>
                            <Suspense fallback={<div style={{display:'flex',justifyContent:'center',alignItems:'center',minHeight:'60vh'}}><div className="spinner-border text-primary" /></div>}>
                              <BelgeKontrolPage />
                            </Suspense>
                          </Layout>
                        </RequireAuthorization>
                      </AuthWrapper>
                    }
                  />
                  <Route
                    path="/belge-kontrol/ziyaret/:visitId"
                    element={
                      <AuthWrapper>
                        {/* Sub-routes must name the menu path explicitly, otherwise they are unrestricted. */}
                        <RequireAuthorization path="/belge-kontrol">
                          <Layout>
                            <Suspense fallback={<div style={{display:'flex',justifyContent:'center',alignItems:'center',minHeight:'60vh'}}><div className="spinner-border text-primary" /></div>}>
                              <BelgeKontrolVisitPage />
                            </Suspense>
                          </Layout>
                        </RequireAuthorization>
                      </AuthWrapper>
                    }
                  />
                  <Route
                    path="/quality-management"
                    element={
                      <AuthWrapper>
                        <RequireAuthorization path="/quality-management">
                          <Layout>
                            <QualityManagementPage />
                          </Layout>
                        </RequireAuthorization>
                      </AuthWrapper>
                    }
                  />
                  <Route
                    path="/employee-management"
                    element={
                      <AuthWrapper>
                        <RequireAuthorization path="/employee-management">
                          <Layout>
                            <EmployeeManagementPage />
                          </Layout>
                        </RequireAuthorization>
                      </AuthWrapper>
                    }
                  />
                  <Route
                    path="/sayfa-yetkileri"
                    element={
                      <AuthWrapper>
                        <RequireAuthorization path="/sayfa-yetkileri">
                          <Layout>
                            <PageAccessManagementPage />
                          </Layout>
                        </RequireAuthorization>
                      </AuthWrapper>
                    }
                  />
                  <Route
                    path="/operation-management"
                    element={
                      <AuthWrapper>
                        <RequireAuthorization path="/operation-management">
                          <Layout>
                            <OperationManagementPage />
                          </Layout>
                        </RequireAuthorization>
                      </AuthWrapper>
                    }
                  />
                  <Route
                    path="/customer-management"
                    element={
                      <AuthWrapper>
                        <RequireAuthorization path="/customer-management">
                          <Layout>
                            <CustomerManagementPage />
                          </Layout>
                        </RequireAuthorization>
                      </AuthWrapper>
                    }
                  />
                  <Route
                    path="/stock-management"
                    element={
                      <AuthWrapper>
                        <RequireAuthorization path="/stock-management">
                          <Layout>
                            <StockManagementPage />
                          </Layout>
                        </RequireAuthorization>
                      </AuthWrapper>
                    }
                  />
                  <Route
                    path="/projects-management"
                    element={
                      <AuthWrapper>
                        <RequireAuthorization path="/projects-management">
                          <Layout>
                            <ProjectsManagementPage />
                          </Layout>
                        </RequireAuthorization>
                      </AuthWrapper>
                    }
                  />
                  <Route
                    path="/center-management"
                    element={
                      <AuthWrapper>
                        <RequireAuthorization path="/center-management">
                          <Layout>
                            <CenterManagementPage />
                          </Layout>
                        </RequireAuthorization>
                      </AuthWrapper>
                    }
                  />
                  <Route
                    path="/external-personnel-management"
                    element={
                      <AuthWrapper>
                        <RequireAuthorization path="/external-personnel-management">
                          <Layout>
                            <ExternalPersonnelManagementPage />
                          </Layout>
                        </RequireAuthorization>
                      </AuthWrapper>
                    }
                  />
                  <Route
                    path="/constant-management"
                    element={
                      <AuthWrapper>
                        <RequireAuthorization path="/constant-management">
                          <Layout>
                            <ConstantManagementPage />
                          </Layout>
                        </RequireAuthorization>
                      </AuthWrapper>
                    }
                  />
                  <Route
                    path="/sponsor-management"
                    element={
                      <AuthWrapper>
                        <RequireAuthorization path="/sponsor-management">
                          <Layout>
                            <SponsorManagementPage />
                          </Layout>
                        </RequireAuthorization>
                      </AuthWrapper>
                    }
                  />
                  <Route
                    path="/employee-insurance"
                    element={
                      <AuthWrapper>
                        <RequireAuthorization path="/employee-insurance">
                          <Layout>
                            <EmployeeInsurancePage />
                          </Layout>
                        </RequireAuthorization>
                      </AuthWrapper>
                    }
                  />
                  <Route
                    path="/customer-tariffs"
                    element={
                      <AuthWrapper>
                        <RequireAuthorization path="/customer-tariffs">
                          <Layout>
                            <CustomerTariffsPage />
                          </Layout>
                        </RequireAuthorization>
                      </AuthWrapper>
                    }
                  />
                  <Route
                    path="/bank-account-management"
                    element={
                      <AuthWrapper>
                        <RequireAuthorization path="/bank-account-management">
                          <Layout>
                            <BankAccountManagementPage />
                          </Layout>
                        </RequireAuthorization>
                      </AuthWrapper>
                    }
                  />
                  <Route
                    path="/salary-payment"
                    element={
                      <AuthWrapper>
                        <RequireAuthorization path="/salary-payment">
                          <Layout>
                            <SalaryPaymentPage />
                          </Layout>
                        </RequireAuthorization>
                      </AuthWrapper>
                    }
                  />
                  <Route
                    path="/budget-management"
                    element={
                      <AuthWrapper>
                        <RequireAuthorization path="/budget-management">
                          <Layout>
                            <BudgetManagementPage />
                          </Layout>
                        </RequireAuthorization>
                      </AuthWrapper>
                    }
                  />
                  <Route
                    path="/smo-lead-reports"
                    element={
                      <AuthWrapper>
                        <RequireAuthorization path="/smo-lead-reports">
                          <Layout>
                            <SMOLeadReports />
                          </Layout>
                        </RequireAuthorization>
                      </AuthWrapper>
                    }
                  />
                  <Route
                    path="/bolge-muduru-reports"
                    element={
                      <AuthWrapper>
                        <RequireAuthorization path="/bolge-muduru-reports">
                          <Layout>
                            <BolgeMudurleriReports />
                          </Layout>
                        </RequireAuthorization>
                      </AuthWrapper>
                    }
                  />
                  <Route
                    path="/employee-reports"
                    element={
                      <AuthWrapper>
                        <RequireAuthorization path="/employee-reports">
                          <Layout>
                            <EmployeeReports />
                          </Layout>
                        </RequireAuthorization>
                      </AuthWrapper>
                    }
                  />
                  <Route
                    path="/employee-analysis"
                    element={
                      <AuthWrapper>
                        <RequireAuthorization path="/employee-analysis">
                          <Layout>
                            <EmployeeAnalysis />
                          </Layout>
                        </RequireAuthorization>
                      </AuthWrapper>
                    }
                  />
                  <Route
                    path="/employee-statistics"
                    element={
                      <AuthWrapper>
                        <RequireAuthorization path="/employee-statistics">
                          <Layout>
                            <EmployeeStatistics />
                          </Layout>
                        </RequireAuthorization>
                      </AuthWrapper>
                    }
                  />
                  <Route
                    path="/education-reports"
                    element={
                      <AuthWrapper>
                        <RequireAuthorization path="/education-reports">
                          <Layout>
                            <EducationReportsPage />
                          </Layout>
                        </RequireAuthorization>
                      </AuthWrapper>
                    } 
                  />
                  <Route
                    path="/data-entry-values"
                    element={
                      <AuthWrapper>
                        <RequireAuthorization path="/data-entry-values">
                          <Layout>
                            <DataEntryValuesPage />
                          </Layout>
                        </RequireAuthorization>
                      </AuthWrapper>
                    }
                  />
                  <Route
                    path="/education-management"
                    element={
                      <AuthWrapper>
                        <RequireAuthorization path="/education-management">
                          <Layout>
                            <EducationManagementPage />
                          </Layout>
                        </RequireAuthorization>
                      </AuthWrapper>
                    }
                  />
                  <Route
                    path="/project-finance-management"
                    element={
                      <AuthWrapper>
                        <RequireAuthorization path="/project-finance-management">
                          <Layout>
                            <ProjectsFinanceManagementPage />
                          </Layout>
                        </RequireAuthorization>
                      </AuthWrapper>
                    }
                  />
                  <Route
                    path="/employee-finance-management"
                    element={
                      <AuthWrapper>
                        <RequireAuthorization path="/employee-finance-management">
                          <Layout>
                            <EmployeeFinanceManagementPage />
                          </Layout>
                        </RequireAuthorization>
                      </AuthWrapper>
                    }
                  />
                  <Route
                    path="/saha-masraflari"
                    element={
                      <AuthWrapper>
                        <RequireAuthorization path="/saha-masraflari">
                          <Layout>
                            <Suspense fallback={<div style={{display:'flex',justifyContent:'center',alignItems:'center',minHeight:'60vh'}}><div className="spinner-border text-primary" /></div>}>
                              <FieldExpensesPage />
                            </Suspense>
                          </Layout>
                        </RequireAuthorization>
                      </AuthWrapper>
                    }
                  />
                  <Route
                    path="/izin-yonetimi"
                    element={
                      <AuthWrapper>
                        <RequireAuthorization path="/izin-yonetimi">
                          <Layout>
                            <Suspense fallback={<div style={{display:'flex',justifyContent:'center',alignItems:'center',minHeight:'60vh'}}><div className="spinner-border text-primary" /></div>}>
                              <LeaveRequestsPage />
                            </Suspense>
                          </Layout>
                        </RequireAuthorization>
                      </AuthWrapper>
                    }
                  />
                  <Route
                    path="/admin-definitions"
                    element={
                      <AuthWrapper>
                        <RequireAuthorization path="/admin-definitions">
                          <Layout>
                            <AdminDefinitionsPage />
                          </Layout>
                        </RequireAuthorization>
                      </AuthWrapper>
                    }
                  />
                  
                  {/* Catch-all route for undefined URLs */}
                  <Route
                    path="*"
                    element={
                      <AuthWrapper>
                        <Layout>
                          <NotFoundPage />
                        </Layout>
                      </AuthWrapper>
                    }
                  />
                </Routes>
              </div>
            </div>
          </Router>
            </MessageNotificationProvider>
            </PendingLeavesProvider>
          </VideoCallProvider>
        </UserProfileProvider>
      </ThemeProvider>
    </GoogleOAuthProvider>
  );
}

export default App;